/* ===================================================================
   useChuteProgress() -- derived state for the LINEAR onboarding CHUTE
   (routes/Welcome, 2026-07-09). The chute is EXACTLY four steps, one primary
   action per full-screen page, AUTO-ADVANCING on completion:

     1. signup   done  = an authenticated session exists (always true once the
                         chute mounts inside RequireAuth).
     2. connect  done  = google_connection_status === 'connected' (canonical:
                         valid token + owner-CONFIRMED listing + >=1 review).
                 skipped = the owner explicitly tapped "skip for now" (distinct
                         from done; resurfaceable; never counts as connected).
     3. test     done  = the owner fired a self-test review email (server-
                         confirmed sent). Card-FREE.
     4. done     terminal celebration -> the dashboard.

   Navigation is driven by nextStep from DERIVED completion (real account data +
   the two honest markers in lib/chuteState), NOT a stored index. So resume is
   automatically correct across devices / reloads / the Google OAuth round-trip.
   =================================================================== */
import { useEffect, useMemo, useState } from 'react';
import { useBusiness } from './useBusiness';
import { useMe } from './useMe';
import { useRequests } from './useRequests';
import { useSessionToken } from './useSessionToken';
import {
  getChuteTestSent,
  getChuteConnectSkipped,
  CHUTE_TEST_SENT_EVENT,
  CHUTE_CONNECT_SKIPPED_EVENT,
} from '../lib/chuteState';

export type ChuteStepId = 'signup' | 'connect' | 'test' | 'done';

export interface ChuteStep {
  id: ChuteStepId;
  /* 1-based number for the "Step X of 4" cue. */
  num: number;
  /* The step's own deep-linkable sub-route under /welcome. */
  route: string;
  done: boolean;
  skipped: boolean;
}

export interface ChuteProgress {
  steps: ChuteStep[];
  /* The first step that is neither done NOR skipped, walking connect -> test.
     Once both are resolved it is the terminal 'done' step. Drives auto-advance
     + resume (route to nextStep.route). */
  nextStep: ChuteStep;
  /* connect + test both resolved (done or, for connect, skipped) -> the chute
     is finished and the owner belongs on the dashboard. */
  complete: boolean;
  /* True while the underlying signals are still loading. Callers must not
     redirect/advance while true (avoids flashing the wrong step). */
  loading: boolean;
}

const ROUTE: Record<ChuteStepId, string> = {
  signup: '/auth',
  connect: '/welcome/connect',
  test: '/welcome/test',
  done: '/welcome/done',
};

export function useChuteProgress(): ChuteProgress {
  useSessionToken();

  const meQ = useMe();
  const businessQ = useBusiness();
  const requestsQ = useRequests({ limit: 1 });

  const clientId = meQ.data?.client_id;

  // Re-render when either honest marker flips (written elsewhere in the app);
  // the marker getters below re-read on that render, so the useMemo re-derives
  // via its testSent/connectSkipped deps -- `tick` itself is not a memo dep.
  const [, setTick] = useState(0);
  useEffect(() => {
    const bump = () => setTick((n) => n + 1);
    window.addEventListener(CHUTE_TEST_SENT_EVENT, bump);
    window.addEventListener(CHUTE_CONNECT_SKIPPED_EVENT, bump);
    return () => {
      window.removeEventListener(CHUTE_TEST_SENT_EVENT, bump);
      window.removeEventListener(CHUTE_CONNECT_SKIPPED_EVENT, bump);
    };
  }, []);

  const testSent = getChuteTestSent(clientId);
  const connectSkipped = getChuteConnectSkipped(clientId);

  return useMemo(() => {
    const googleStatus =
      businessQ.data?.google_connection?.google_connection_status ??
      meQ.data?.google_connection_status ??
      'not_started';
    const googleConnected = googleStatus === 'connected';

    // A real first request sent ALSO satisfies "test done" (a returning owner
    // who already activated should never be pushed back through the chute).
    const requestSent = (requestsQ.data?.count ?? 0) >= 1;
    const testDone = testSent || requestSent;

    const steps: ChuteStep[] = [
      { id: 'signup', num: 1, route: ROUTE.signup, done: true, skipped: false },
      {
        id: 'connect',
        num: 2,
        route: ROUTE.connect,
        done: googleConnected,
        skipped: connectSkipped && !googleConnected,
      },
      { id: 'test', num: 3, route: ROUTE.test, done: testDone, skipped: false },
      { id: 'done', num: 4, route: ROUTE.done, done: false, skipped: false },
    ];

    const connectResolved = googleConnected || connectSkipped;
    const nextStep = !connectResolved
      ? steps[1]
      : !testDone
        ? steps[2]
        : steps[3];

    const complete = connectResolved && testDone;

    const loading =
      meQ.isLoading || businessQ.isLoading || requestsQ.isLoading;

    return { steps, nextStep, complete, loading };
  }, [
    meQ.data,
    meQ.isLoading,
    businessQ.data,
    businessQ.isLoading,
    requestsQ.data,
    requestsQ.isLoading,
    testSent,
    connectSkipped,
  ]);
}
